import { normalizeCountryLabel } from "@/shared/config/localisation";

import { getPeriodGranularity } from "../constants/invoicePeriod";
import { INVOICE_LOCALISATION } from "../constants/localisation";
import type {
  ApiInvoiceRow,
  InvoiceDetail,
  InvoiceLineFilter,
  InvoiceLineItem,
  InvoiceParty,
  InvoicePeriodGranularity,
  InvoicePeriodOption,
  InvoicePreviewData,
  InvoiceTaxLine,
  InvoiceTaxSplit,
} from "../types";

const EMPTY_VALUE = "—";

export const BROKER_COUNTRY = INVOICE_LOCALISATION.brokerCountry;

export const TAX_RATE_PERCENT = INVOICE_LOCALISATION.taxRatePercent;

const round2 = (value: number) => Math.round(value * 100) / 100;

const toAmount = (value: unknown): number => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

/** The ledger only sends the insurer country once it is billed; otherwise it is the broker's own. */
export const getInsurerCountry = (row?: ApiInvoiceRow | null): string =>
  normalizeCountryLabel(row?.insurer_country) || BROKER_COUNTRY;

const getFiscalYearStart = (date: Date) =>
  date.getUTCMonth() >= 3 ? date.getUTCFullYear() : date.getUTCFullYear() - 1;

const toPeriodKey = (value?: string, granularity: InvoicePeriodGranularity = "monthly") => {
  if (!value) return "";

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";

  const year = parsed.getUTCFullYear();

  if (granularity === "yearly") {
    return `FY${getFiscalYearStart(parsed)}`;
  }

  if (granularity === "quarterly") {
    return `${year}-Q${Math.floor(parsed.getUTCMonth() / 3) + 1}`;
  }

  return `${year}-${String(parsed.getUTCMonth() + 1).padStart(2, "0")}`;
};

const toPeriodLabel = (periodKey: string) => {
  if (periodKey.startsWith("FY")) {
    const startYear = Number(periodKey.replace("FY", ""));
    return `FY ${startYear}-${String(startYear + 1).slice(-2)}`;
  }

  if (periodKey.includes("Q")) {
    const [year, quarter] = periodKey.split("-");
    return `${quarter} ${year}`;
  }

  const [year, month] = periodKey.split("-");
  const parsed = new Date(Number(year), Number(month) - 1, 1);

  return parsed.toLocaleDateString("en-GB", { month: "short", year: "numeric" });
};

/** Ledger rows carry `period_from` only once invoiced, so fall back to the policy issue date. */
const getRowPeriodSource = (row: ApiInvoiceRow) => row.period_from || row.issue_date;

const PERIOD_OPTION_COUNT: Record<InvoicePeriodGranularity, number> = {
  monthly: 12,
  quarterly: 8,
  yearly: 3,
};

/** Periods are offered from the calendar, not from the loaded page of ledger rows. */
export const getInvoicePeriodOptions = (
  granularity: InvoicePeriodGranularity
): InvoicePeriodOption[] => {
  const now = new Date();
  const keys: string[] = [];

  for (let index = 0; index < PERIOD_OPTION_COUNT[granularity]; index += 1) {
    const reference = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));

    if (granularity === "yearly") {
      reference.setUTCFullYear(reference.getUTCFullYear() - index);
    } else {
      reference.setUTCMonth(
        reference.getUTCMonth() - index * (granularity === "quarterly" ? 3 : 1)
      );
    }

    const key = toPeriodKey(reference.toISOString(), granularity);
    if (key && !keys.includes(key)) keys.push(key);
  }

  return keys.map((key) => ({ value: key, label: toPeriodLabel(key) }));
};

export const getDefaultPeriodForFilter = (
  filterType: string,
  options: InvoicePeriodOption[]
): string => {
  const now = new Date();
  const reference = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));

  if (filterType === "previous_month") {
    reference.setUTCMonth(reference.getUTCMonth() - 1);
  } else if (filterType === "previous_quarter") {
    reference.setUTCMonth(reference.getUTCMonth() - 3);
  } else if (filterType === "previous_fy") {
    reference.setUTCFullYear(reference.getUTCFullYear() - 1);
  }

  const key = toPeriodKey(reference.toISOString(), getPeriodGranularity(filterType));

  return options.some((option) => option.value === key) ? key : options[0]?.value || "";
};

export const getInvoiceTaxSplit = (insurerCountry: string): InvoiceTaxSplit => {
  const { taxLabel, hasStateSplit } = INVOICE_LOCALISATION;
  const isCrossBorder = insurerCountry.trim().toLowerCase() !== BROKER_COUNTRY.trim().toLowerCase();

  const label = hasStateSplit
    ? isCrossBorder
      ? "IGST (cross-border)"
      : "CGST + SGST (domestic)"
    : `${taxLabel} (${isCrossBorder ? "cross-border" : "domestic"})`;

  return {
    brokerCountry: BROKER_COUNTRY,
    insurerCountry,
    isCrossBorder,
    taxLabel,
    label,
    ratePercent: TAX_RATE_PERCENT,
  };
};

export const buildInvoiceTaxBreakdown = (
  taxSplit: InvoiceTaxSplit,
  totalTax: number
): InvoiceTaxLine[] => {
  const { hasStateSplit } = INVOICE_LOCALISATION;
  const { ratePercent, isCrossBorder, taxLabel } = taxSplit;

  if (!hasStateSplit) {
    return [{ label: `${taxLabel} @ ${ratePercent}%`, amount: totalTax }];
  }

  if (isCrossBorder) {
    return [{ label: `IGST @ ${ratePercent}%`, amount: totalTax }];
  }

  const halfTax = round2(totalTax / 2);

  return [
    { label: `CGST @ ${ratePercent / 2}%`, amount: halfTax },
    { label: `SGST @ ${ratePercent / 2}%`, amount: round2(totalTax - halfTax) },
  ];
};

/**
 * The ledger sends the tax amount on `tax_type`. It is only trusted when it agrees with the
 * localised rate, otherwise the line is taxed at the configured GST/VAT percentage.
 */
const resolveLineTax = (row: ApiInvoiceRow, commission: number, taxRatePercent: number) => {
  const expectedTax = round2((commission * taxRatePercent) / 100);

  if (row.tax_type === undefined || row.tax_type === null || row.tax_type === "") {
    return expectedTax;
  }

  const apiTax = Number(row.tax_type);
  if (!Number.isFinite(apiTax) || apiTax < 0) return expectedTax;

  const tolerance = Math.max(0.05, Math.abs(expectedTax) * 0.02);

  return Math.abs(apiTax - expectedTax) <= tolerance ? round2(apiTax) : expectedTax;
};

export const buildInvoiceLineItem = (
  row: ApiInvoiceRow,
  taxRatePercent: number = TAX_RATE_PERCENT
): InvoiceLineItem => {
  const commission = toAmount(row.commission ?? row.invoice_amount);
  const premium = toAmount(row.premium);
  const ratePercent = toAmount(row.rate) || (premium ? round2((commission * 100) / premium) : 0);

  return {
    id: String(row.id ?? row.policy_id ?? row.policy_no ?? ""),
    policyNo: String(row.policy_no ?? "-"),
    product: row.product || "-",
    customer: row.customer || "-",
    issueDate: row.issue_date || "",
    premium,
    ratePercent,
    commission,
    tax: resolveLineTax(row, commission, taxRatePercent),
  };
};

export const getInvoiceLineTax = (line: InvoiceLineItem, taxRatePercent = TAX_RATE_PERCENT) =>
  line.tax !== undefined ? line.tax : round2((line.commission * taxRatePercent) / 100);

const matchesInsurer = (row: ApiInvoiceRow, insurerId?: string, insurerName?: string) => {
  const rowId = String(row.insurer_id ?? "").trim();
  const rowName = String(row.insurer ?? "")
    .trim()
    .toLowerCase();

  // No insurer selected (OC environment hides the field) means every insurer is in scope.
  if (!insurerId && !insurerName) return true;

  if (insurerId && rowId) return rowId === String(insurerId).trim();

  return Boolean(insurerName) && rowName === String(insurerName).trim().toLowerCase();
};

export const getInvoiceLineRows = (
  { insurerId, insurerName, period, granularity }: InvoiceLineFilter,
  rows: ApiInvoiceRow[]
): ApiInvoiceRow[] => {
  if (!period) return [];

  return rows.filter(
    (row) =>
      matchesInsurer(row, insurerId, insurerName) &&
      toPeriodKey(getRowPeriodSource(row), granularity) === period
  );
};

export const buildInvoicePreview = (
  filter: InvoiceLineFilter,
  rows: ApiInvoiceRow[]
): InvoicePreviewData => {
  const matchedRows = getInvoiceLineRows(filter, rows);
  const taxSplit = getInvoiceTaxSplit(getInsurerCountry(matchedRows[0]));

  const lines = matchedRows.map((row) => buildInvoiceLineItem(row, taxSplit.ratePercent));

  const commissionTotal = round2(lines.reduce((sum, line) => sum + line.commission, 0));
  const taxTotal = round2(
    lines.reduce((sum, line) => sum + getInvoiceLineTax(line, taxSplit.ratePercent), 0)
  );
  const grandTotal = round2(commissionTotal + taxTotal);

  return {
    lines,
    lineCount: lines.length,
    commissionTotal,
    taxTotal,
    grandTotal,
    taxSplit,
    taxBreakdown: buildInvoiceTaxBreakdown(taxSplit, taxTotal),
  };
};

const BROKER_TAX_ID_BY_LOCALISATION: Record<string, string> = {
  IN: "08AABCN1234F1Z5",
  AE: "100123456700003",
  SA: "300123456700003",
};

/** Broker's own registration details — static config until a broker-profile API exists. */
export const BILLED_BY: InvoiceParty = {
  name: "NexInsure",
  country: BROKER_COUNTRY,
  taxId: BROKER_TAX_ID_BY_LOCALISATION[INVOICE_LOCALISATION.code] || EMPTY_VALUE,
  address: "New Ahinsapuri, Fatehpura, Udaipur, Rajasthan - 313001",
};

export const getInvoiceDetail = (row?: ApiInvoiceRow | null): InvoiceDetail | null => {
  if (!row) return null;

  const taxSplit = getInvoiceTaxSplit(getInsurerCountry(row));

  const lineItems = [buildInvoiceLineItem(row, taxSplit.ratePercent)];
  const taxableValue = round2(lineItems.reduce((sum, line) => sum + line.commission, 0));
  const totalTax = round2(
    lineItems.reduce((sum, line) => sum + getInvoiceLineTax(line, taxSplit.ratePercent), 0)
  );

  const periodFrom = row.period_from || row.issue_date || "";
  const grandTotal = round2(taxableValue + totalTax);
  const isPaid = String(row.status ?? "").toUpperCase() === "PAID";
  const totalCollected =
    row.total_collected !== undefined ? toAmount(row.total_collected) : isPaid ? grandTotal : 0;

  return {
    invoiceNo: String(row.invoice_no ?? row.invoice ?? "-"),
    status: String(row.status ?? "UNBILLED").toUpperCase() as InvoiceDetail["status"],
    period: `${periodFrom}|${row.period_to || periodFrom}`,
    invoiceDate: row.issue_date || "",
    dueDate: row.due_date || "",
    billedBy: BILLED_BY,
    billedTo: {
      name: row.insurer || "-",
      country: getInsurerCountry(row),
      taxId: row.insurer_tax_id || EMPTY_VALUE,
      address: row.insurer_address || EMPTY_VALUE,
    },
    lineItems,
    taxSplit,
    taxableValue,
    taxBreakdown: buildInvoiceTaxBreakdown(taxSplit, totalTax),
    totalTax,
    grandTotal,
    totalCollected,
    outstanding:
      row.outstanding !== undefined
        ? toAmount(row.outstanding)
        : Math.max(round2(grandTotal - totalCollected), 0),
  };
};
